import type { Dispatch, SetStateAction } from "react";

import {
    AppointmentNotificationPayload,
    AppointmentNotificationType,
    AppointmentStatusUpdatePayload,
} from "@/types/call.types";
import { hasStringProperty, isRecord } from "@/utils/type-guards";

import type { NotificationAdapter } from "./base/notification-adapter.interface";

const isAppointmentStatusUpdatePayload = (
    payload: Record<string, unknown>
): payload is AppointmentStatusUpdatePayload =>
    payload.type === AppointmentNotificationType.STATUS_UPDATE &&
    hasStringProperty(payload, "appointmentId") &&
    hasStringProperty(payload, "brandName") &&
    (payload.leadId === undefined || typeof payload.leadId === "string");

const isAppointmentRemovalPayload = (
    payload: Record<string, unknown>
): payload is Extract<AppointmentNotificationPayload, { type: AppointmentNotificationType.REMOVAL }> =>
    payload.type === AppointmentNotificationType.REMOVAL &&
    hasStringProperty(payload, "appointmentId") &&
    hasStringProperty(payload, "brandName");

const isAppointmentNotificationPayload = (payload: unknown): payload is AppointmentNotificationPayload => {
    if (!isRecord(payload) || typeof payload.type !== "string") {
        return false;
    }

    if (payload.type === AppointmentNotificationType.STATUS_UPDATE) {
        return isAppointmentStatusUpdatePayload(payload);
    }

    if (payload.type === AppointmentNotificationType.REMOVAL) {
        return isAppointmentRemovalPayload(payload);
    }

    return false;
};

export const createAppointmentNotificationAdapter = ({
    handleAppointmentStatusUpdate,
    handleAppointmentRemoval,
    setPendingRecordId,
}: {
    handleAppointmentStatusUpdate: (
        notification: AppointmentStatusUpdatePayload,
        onDetailsClick: (leadId: string) => void
    ) => void;
    handleAppointmentRemoval: (appointmentId: string) => void;
    setPendingRecordId: Dispatch<SetStateAction<string | null>>;
}): NotificationAdapter<AppointmentNotificationPayload> => {
    return {
        canHandle: isAppointmentNotificationPayload,
        process: (payload: unknown) => {
            if (!isAppointmentNotificationPayload(payload)) {
                return;
            }
            if (payload.type === AppointmentNotificationType.STATUS_UPDATE) {
                handleAppointmentStatusUpdate(payload, (leadId: string) => setPendingRecordId(leadId));
                return;
            }
            handleAppointmentRemoval(payload.appointmentId);
        },
    };
};
